import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    PlayArrow as PlayArrowIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useTutorialContext } from '../../contexts/TutorialContext';

const AlarmDemoModal = ({ open, onClose }) => {
  const { t } = useTranslation();
    const theme = useTheme();
    const { user } = useAuth();
    const {
        isActive,
        demoContact,
        startTutorial,
        resetTutorial,
        setDemoContact,
        DEMO_NAMES,
    } = useTutorialContext();

    const [localEmail, setLocalEmail] = useState(demoContact?.email || user?.email || '');
    const [localPhone, setLocalPhone] = useState(demoContact?.phone || '');

    const isArsenalUser = (user?.email || '').toLowerCase().endsWith(t('hardcoded.components_common_sidebar.arsenalmx'));

    const handleStart = () => {
        setDemoContact({ email: localEmail, phone: localPhone });
        startTutorial();
        onClose();
    };

    const handleReset = () => {
        resetTutorial();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    border: `2px solid ${theme.palette.error.main}`,
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: theme.palette.error.main }}>
                            {i18n.t('demo.title')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('hardcoded.components_demo_alarmdemomodal.vive_la_experiencia_completa_de')}</Typography>
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2.5}>
                    {!isArsenalUser && (
                        <Alert severity="warning">
                            {t('hardcoded.components_demo_alarmdemomodal.solo_usuarios_arsenalmx_pueden_usar')}</Alert>
                    )}

                    <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                            {t('hardcoded.components_demo_alarmdemomodal.en_esta_demo_guiada_vas')}</Typography>
                        <Box component="ol" sx={{ pl: 2, m: 0, '& li': { mb: 0.5, fontSize: '0.85rem' } }}>
                            <li>{t('hardcoded.components_demo_alarmdemomodal.crear_un_vehiculo')}{DEMO_NAMES?.vehicle})</li>
                            <li>{t('hardcoded.components_demo_alarmdemomodal.registrar_un_receptor')}{DEMO_NAMES?.receiverMac})</li>
                            <li>{i18n.t('gps.form.register')}{DEMO_NAMES?.sensorCount} {i18n.t('sensors.title')}</li>
                            <li>{t('hardcoded.components_demo_alarmdemomodal.asignar_sensores_y_receptor_al')}</li>
                            <li>{t('hardcoded.components_demo_alarmdemomodal.configurar_un_perfil_de_alarmas')}</li>
                            <li>{t('hardcoded.components_demo_alarmdemomodal.visualizar_el_esquematico_en_vivo')}</li>
                            <li>{t('hardcoded.components_demo_alarmdemomodal.activar_simulacion_y_ver_alarmas')}</li>
                            <li>{t('hardcoded.components_demo_alarmdemomodal.revisar_tickets_y_recibir_notificaciones')}</li>
                        </Box>
                    </Alert>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {t('hardcoded.components_demo_alarmdemomodal.datos_de_contacto_para_notificaciones')}</Typography>

                    <TextField
                        size="small"
                        label={t('hardcoded.components_demo_alarmdemomodal.email_para_notificaciones')}
                        value={localEmail}
                        onChange={(event) => setLocalEmail(event.target.value)}
                        placeholder={t('hardcoded.components_demo_alarmdemomodal.nombrearsenalmx')}
                        disabled={!isArsenalUser}
                        fullWidth
                        helperText={t('hardcoded.components_demo_alarmdemomodal.recibiras_alertas_por_correo_cuando')}
                    />
                    <TextField
                        size="small"
                        label={t('hardcoded.components_demo_alarmdemomodal.telefono_para_whatsapp_y_llamada')}
                        value={localPhone}
                        onChange={(event) => setLocalPhone(event.target.value)}
                        placeholder="+5210000000000"
                        disabled={!isArsenalUser}
                        fullWidth
                        helperText={t('hardcoded.components_demo_alarmdemomodal.opcional_recibiras_whatsapp_y_una')}
                    />

                    {isActive && (
                        <Alert severity="success">
                            {t('hardcoded.components_demo_alarmdemomodal.la_demo_guiada_esta_activa')}</Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                {isActive && (
                    <Button variant="outlined" color="warning" startIcon={<RefreshIcon />} onClick={handleReset}>
                        {i18n.t('hardcoded.components_demo_alarmdemomodal.reiniciar')}</Button>
                )}

                <Button
                    variant="contained"
                    color="error"
                    size="large"
                    startIcon={<PlayArrowIcon />}
                    onClick={handleStart}
                    disabled={!isArsenalUser}
                    sx={{ fontWeight: 800, px: 4 }}
                >
                    {isActive ? t('hardcoded.components_demo_alarmdemomodal.reiniciar_demo_guiada') : t('hardcoded.components_demo_alarmdemomodal.iniciar_demo_guiada')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AlarmDemoModal;
